import React from 'react'

import { SxProps } from '@mui/material'

import { useQuery, useParams } from 'utils/hooks'
import { useEntity } from 'redux/hooks/Entities'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Divider from 'components_new/atoms/Divider'

import ContentBox from 'components_new/molecules/ContentBox'
import ContentfulRichTextRendered from 'components_new/molecules/ContentfulRichTextRenderer'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'

import EntityMetrics from 'components_new/organisms/EntityMetrics'
import EntityProperties from 'components_new/organisms/EntityProperties'
import EntitySlices from 'components_new/organisms/EntitySlices'
import EntityTerminology from 'components_new/organisms/EntityTerminology'

import Loading from './loading'
import { useTranslation } from 'language/translator'

interface Props {
  sx?: SxProps
}

const EntitySettings = React.memo((props: Props) => {
  const { sx } = props
  const { id } = useParams<{ id: string }>()
  const { t, lang } = useTranslation()

  const entity = useEntity(id)
  const { query } = useQuery()

  const edit = Boolean(query['edit'])

  if (!entity) {
    return <Loading />
  }

  // success
  return (
    <Box sx={{ ...sx }}>
      {/*-- about --*/}
      <ContentBox
        collapse={{
          active: true,
          unmountOnExit: true
        }}
        color="default"
        gutterBottom={true}
        header={{
          icon: { name: 'Info' },
          title: t('global', 'about')
        }}
      >
        <ContentfulRichTextRendered value={entity.body[lang]} variant="body2" />
      </ContentBox>

      {/*-- dim-only notice / columns + slices --*/}
      {entity.is_dim_only ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('entities', 'dim_only_notice')}
        </Alert>
      ) : (
        <>
          {/*-- columns --*/}
          <ContentBox
            collapse={{
              active: true,
              defaultCollapsed: true
            }}
            color="default"
            editMode={edit}
            gutterBottom={true}
            header={{
              icon: { name: 'Table' },
              title: t('global', 'columns'),
              titleAdornment: (
                <Chip label={entity.columns.standard.length} size="small" />
              )
            }}
            helperText={t('entities', 'properties_column_description')}
          >
            <Divider sx={{ my: 2 }} />

            <EntityProperties />
          </ContentBox>

          {/*-- slices --*/}
          <ContentBox
            collapse={{
              active: true,
              defaultCollapsed: true
            }}
            color="default"
            editMode={edit}
            gutterBottom={true}
            header={{
              icon: { name: 'Slices' },
              title: t('entities', 'slices'),
              titleAdornment: (
                <Chip label={entity.slices.all.length} size="small" />
              )
            }}
            helperText={t('entities', 'slices_definition_description')}
          >
            <Divider sx={{ my: 2 }} />

            <EntitySlices edit={edit} id={id} />
          </ContentBox>
        </>
      )}

      {/*-- terminology --*/}
      <ContentBox
        collapse={{
          active: true,
          defaultCollapsed: true
        }}
        color="default"
        editMode={edit}
        gutterBottom={true}
        header={{
          icon: { name: 'TitleOutlined' },
          title: t('entities', 'terminology'),
          titleAdornment: (
            <Chip label={entity.terminology.length} size="small" />
          )
        }}
        helperText={t('entities', 'terminology_description')}
      >
        {entity.terminology.length > 0 ? (
          <EntityTerminology />
        ) : (
          <EmptyPlaceholder
            background={true}
            title={t('entities', 'no_terms_to_customize')}
          />
        )}
      </ContentBox>

      {/*-- metrics --*/}
      <ContentBox
        collapse={{
          active: true,
          defaultCollapsed: true,
          unmountOnExit: true
        }}
        color="default"
        gutterBottom={true}
        header={{
          icon: { name: 'Metrics' },
          title: t('metrics', 'metrics'),
          titleAdornment: (
            <Chip label={entity.metrics.all.length} size="small" />
          )
        }}
        helperText={t('entities', 'metric_dependency_description')}
      >
        <EntityMetrics entity={entity} />
      </ContentBox>
    </Box>
  )
})

export default EntitySettings
